// LessonViewBase.tsx — self-contained, embeddable Petcademy lesson renderer.
//
// Sizes to its parent (width 100%). No viewport units, no fixed chrome, no body
// styles, no MUI, no routing. All colors/fonts come from --pc-lesson-* tokens
// (tokens.css) so hosts can re-theme via `className`.
//
// Content comes from the Claude Design prototype via tools/lesson-extract; this
// component renders it and never derives or mutates it.
//
// Two deliberate differences from the prototype, both decided by Product:
//   1. No custom fullscreen button over videos — Vimeo's own control does it.
//   2. No bookmark or scroll-away chrome — that belongs to the hub wrapper,
//      because the public lesson page has neither.
import React from 'react';
import { LessonBlock, LessonDoc, LessonMeta } from './types';
import RelatedLessons from './RelatedLessons';
import ChapterIndex from './blocks/ChapterIndex';
import ArrowList from './blocks/ArrowList';
import ContentCard from './blocks/ContentCard';
import DefaultBlock from './blocks/DefaultBlock';
import FeatureGrid from './blocks/FeatureGrid';
import ImageCard from './blocks/ImageCard';
import InsightList from './blocks/InsightList';
import PracticeCard from './blocks/PracticeCard';
import './tokens.css';
import './lesson.css';

export interface LessonViewBaseProps {
  lesson: LessonDoc;
  /** Already-resolved related lessons (the caller owns the catalog). */
  relatedLessons?: LessonMeta[];
  /** Called with a lesson slug when the reader picks a related lesson. */
  onNavigate?: (slug: string) => void;
  /** Extra class on the root, for host theming. */
  className?: string;
  /** Inline style on the root — theme tokens must sit on .pc-lesson itself. */
  style?: React.CSSProperties;
  /** Hide the Related Lessons section (partner trial). Default true. */
  showRelated?: boolean;
  /** Renders a Back button above the title. */
  onBack?: () => void;
}

// Image cards are routed first: they carry no `variant` at all.
function BlockBody({ block }: { block: LessonBlock }) {
  if (block.isImageCard) return <ImageCard block={block} />;
  switch (block.variant) {
    case 'featureGrid': return <FeatureGrid block={block} />;
    case 'arrowList': return <ArrowList block={block} />;
    case 'insightList': return <InsightList block={block} />;
    case 'practiceCard': return <PracticeCard block={block} />;
    case 'contentCard': return <ContentCard block={block} />;
    case 'default': return <DefaultBlock block={block} />;
    default: return <DefaultBlock block={block} />;
  }
}

// Only default/featureGrid/arrowList/insightList set showTitle; contentCard and
// practiceCard render their own setupTitle as the h2 instead (see the data
// contract in the plan), so this must test showTitle for truthiness.
function Block({ block }: { block: LessonBlock }) {
  return (
    // pc-lesson-block marks content blocks apart from the chapter index and
    // related-lessons sections, which share the same card shell.
    <section className="pc-lesson-card pc-lesson-block">
      {!!block.showTitle && !!block.title && (
        <h2 className="pc-lesson-card-title">{block.title}</h2>
      )}
      <BlockBody block={block} />
    </section>
  );
}

export default function LessonViewBase({
  lesson, relatedLessons = [], onNavigate, className, style, showRelated = true, onBack,
}: LessonViewBaseProps) {
  return (
    <div className={['pc-lesson', className].filter(Boolean).join(' ')} style={style}>
      {/* The hero is a full-width accent band, so it sits outside the 780px
          content column — matching the design prototype on both surfaces. */}
      <header className="pc-lesson-hero">
        <div className="pc-lesson-hero-inner">
          {onBack && (
            <button type="button" className="pc-lesson-back" onClick={onBack}>
              <span className="pc-lesson-back-icon" aria-hidden="true">arrow_back</span>
              Back
            </button>
          )}
          <h1 className="pc-lesson-h1">{lesson.title}</h1>
          {lesson.summary && <p className="pc-lesson-summary">{lesson.summary}</p>}
          {lesson.heroImage && (
            <figure className="pc-lesson-hero-figure">
              <img className="pc-lesson-hero-img" src={lesson.heroImage} alt="" />
            </figure>
          )}
        </div>
      </header>

      <div className="pc-lesson-col">
        <ChapterIndex chapters={lesson.chapters} />

        {lesson.blocks.map((block, i) => (
          /* eslint-disable-next-line react/no-array-index-key */
          <Block key={i} block={block} />
        ))}

        {showRelated && <RelatedLessons lessons={relatedLessons} onNavigate={onNavigate} />}
      </div>
    </div>
  );
}
